import React from 'react';
import { BwjPageContent } from '../../../../../framework/components/bwj-page-content';
import { getTheme, Icon, IStyle, ITheme, mergeStyleSets } from '@fluentui/react';
import { xlate } from '../../../../../framework/utils/i18n';
import { BwjButton, BwjButtonType } from '../../../../../framework/components/bwj-button';
import * as formService from '../../../form-services/presentation-editor';
import { IPresentationEditorFormModel } from '../../../form-services/presentation-editor-iface';
import { secondsToDurationString } from '../../../../utils';
import { EndingSpecMode } from '../../../entities/ending-spec-mode';
import { EndTimeSelector } from '../../../components/end-time-selector';
import { BwjSemiFlexContainer } from '../../../../../framework/components/bwj-semi-flex-container';

interface IUIProps {
  formModel: IPresentationEditorFormModel;
  durationOffsetEndTime: Date;
  onUseDurationOffset: () => void;
  onSpecifyEndTime: () => void;
  onStartPresentation: () => void;
  twelveHourTimeDisplay: boolean;
}

const getClasses = (theme: ITheme) => {
  const title: IStyle = {
    margin: 0,
    fontSize: theme.fonts.xLarge.fontSize,
    color: theme.palette.white,
    fontWeight: 400,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const main: IStyle = {
    margin: 0,
    marginTop: '2em',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.palette.white,
    fontSize: 20,
    fontWeight: 400,
  };

  const rocket: IStyle = {
    fontSize: 125,
  };

  const tagline: IStyle = {
    fontSize: 40,
    fontWeight: 'bold',
    textAlign: 'center',
    margin: 0,
    marginTop: '0.5em',
    marginBottom: '1em',
  };

  const endingLabel: IStyle = {
    display: 'block',
    marginBottom: '1em',
  };

  const optionContainer: IStyle = {
    height: '2.5em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  const dividerContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    maxWidth: 400,
    margin: '0.5em 0',
  };

  const divider: IStyle = {
    height: 0,
    margin: 0,
    padding: 0,
    borderTop: `1px solid ${theme.palette.white}`,
    flexGrow: 1,
  };

  const startContainer: IStyle = {
    margin: 0,
    marginTop: 30,
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    color: theme.palette.white,
    fontSize: 20,
  };

  return mergeStyleSets({
    title,
    main,
    rocket,
    tagline,
    endingLabel,
    optionContainer,
    dividerContainer,
    divider,
    startContainer,
  });
}

const optionButtonStyles = {
  root: {
    fontSize: '1em',
  }
};

const startButtonStyles = {
  root: {
    fontSize: '1em',
    width: '100%',
    maxWidth: 400,
  },
  label: {
    fontWeight: 'bold',
  },
};

export const UI: React.FunctionComponent<IUIProps> = (props) => {
  const theme = getTheme();
  const classes = getClasses(theme);

  return (
    <BwjPageContent
      contentDescription='primary content (no subcontent)'
      backgroundColor={theme.palette.themeSecondary}
      style={{width: '100%', boxSizing: 'border-box', }}
    >
      <BwjSemiFlexContainer
        pinWidth={true}
      >
        <h1 className={classes.title}>{props.formModel.title.value}</h1>
      </BwjSemiFlexContainer>
      <div className={classes.main}>
        <Icon iconName='Rocket' className={classes.rocket} aria-hidden />
        <h2 className={classes.tagline}>{xlate('Get ready for takeoff!')}</h2>
        <span className={classes.endingLabel}>{xlate('Discussion end time:')}</span>
        <div className={classes.optionContainer}>
          {props.formModel.endingSpecMode === EndingSpecMode.Unspecified &&
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            styles={optionButtonStyles}
            text={xlate('{duration} from start time', { duration: secondsToDurationString(props.formModel.duration)})}
            onClick={props.onUseDurationOffset}
          />}
          {props.formModel.endingSpecMode === EndingSpecMode.DurationOffset &&
          <span>
            {xlate('currently {endTime:d-hhmmaa}', { endTime: props.durationOffsetEndTime })}
          </span>}
          {props.formModel.endingSpecMode === EndingSpecMode.ExplicitlyDefined &&
          <EndTimeSelector
            id={formService.presenterDefinedEndTime.id}
            required={false}
            minDateTime={props.formModel.minEndDateTime}
            maxDateTime={props.formModel.maxEndDateTime}
            twelveHourDisplay={props.twelveHourTimeDisplay}
            input={props.formModel.presenterDefinedEndTime}
            onUpdate={formService.presenterDefinedEndTime.onUpdate}
          />}
        </div>
        {props.formModel.hasMultipleEndTimeOptions &&
        <div className={classes.dividerContainer}>
          <div className={classes.divider} />
          <span>{xlate('or')}</span>
          <div className={classes.divider} />
        </div>}
        {props.formModel.hasMultipleEndTimeOptions &&
        <div className={classes.optionContainer}>
          {props.formModel.endingSpecMode !== EndingSpecMode.ExplicitlyDefined &&
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            styles={optionButtonStyles}
            text={xlate('Set a fixed end time...')}
            onClick={props.onSpecifyEndTime}
          />}
          {props.formModel.endingSpecMode === EndingSpecMode.ExplicitlyDefined &&
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            styles={optionButtonStyles}
            text={xlate('{duration} from start time', { duration: secondsToDurationString(props.formModel.duration)})}
            onClick={props.onUseDurationOffset}
          />}
        </div>}
      </div>
      {props.formModel.endingSpecMode !== EndingSpecMode.Unspecified &&
      <div className={classes.startContainer}>
        <BwjButton
          buttonStyle={BwjButtonType.DefaultStandard}
          styles={startButtonStyles}
          text={xlate('Start discussion')}
          onClick={props.onStartPresentation}
        />
      </div>}
    </BwjPageContent>
  );
};
